<template>
	<div
		ref="container"
		class="page-background"
		:style="{ '--bg-transition': `${transitionMs}ms` }"
	></div>
</template>

<script>
import { backgrounds, routes, transitionMs } from "@/data/info/backgrounds.js";

// One lazy chunk per Vanta effect, so only the effects in use are downloaded.
const loaders = {
	birds: () => import("vanta/dist/vanta.birds.min"),
	cells: () => import("vanta/dist/vanta.cells.min"),
	clouds: () => import("vanta/dist/vanta.clouds.min"),
	clouds2: () => import("vanta/dist/vanta.clouds2.min"),
	dots: () => import("vanta/dist/vanta.dots.min"),
	fog: () => import("vanta/dist/vanta.fog.min"),
	globe: () => import("vanta/dist/vanta.globe.min"),
	halo: () => import("vanta/dist/vanta.halo.min"),
	net: () => import("vanta/dist/vanta.net.min"),
	rings: () => import("vanta/dist/vanta.rings.min"),
	ripple: () => import("vanta/dist/vanta.ripple.min"),
	topology: () => import("vanta/dist/vanta.topology.min"),
	trunk: () => import("vanta/dist/vanta.trunk.min"),
	waves: () => import("vanta/dist/vanta.waves.min"),
};

const shared = {
	mouseControls: false,
	touchControls: false,
	gyroControls: false,
	minHeight: 200.0,
	minWidth: 200.0,
	scale: 1.0,
	scaleMobile: 1.0,
};

export default {
	data() {
		return {
			transitionMs,
		};
	},
	created() {
		// Non-reactive bookkeeping: Vanta instances must not be proxied by Vue.
		this.currentKey = null;
		this.active = null;
		this.swapToken = 0;
	},
	watch: {
		"$route.path": {
			handler(path) {
				this.show(routes[path] || "default");
			},
			immediate: true,
		},
	},
	beforeUnmount() {
		this.active?.effect?.destroy();
	},
	methods: {
		async show(key) {
			if (key === this.currentKey) return;
			this.currentKey = key;
			const token = ++this.swapToken;
			const config = backgrounds[key] || backgrounds.default;

			const layer = document.createElement("div");
			layer.className = "bg-layer";
			await this.$nextTick();
			this.$refs.container.appendChild(layer);

			let effect = null;
			try {
				const create = (await loaders[config.effect]()).default;
				// A newer swap started while this effect was downloading.
				if (token !== this.swapToken) {
					layer.remove();
					return;
				}
				effect = create({ el: layer, THREE: window.THREE, ...shared, ...config.options });
			} catch (err) {
				console.error(`Background "${key}" failed to start`, err);
			}

			// Fade the new layer in over the old one, then tear the old one down.
			requestAnimationFrame(() => layer.classList.add("visible"));
			const previous = this.active;
			this.active = { layer, effect };
			if (previous) {
				setTimeout(() => {
					previous.effect?.destroy();
					previous.layer.remove();
				}, transitionMs);
			}
		},
	},
};
</script>

<style>
.page-background {
	position: fixed;
	inset: 0;
	z-index: -1;
	background-color: #1a1a1f;
}

.page-background .bg-layer {
	position: absolute;
	inset: 0;
	opacity: 0;
	transition: opacity var(--bg-transition) ease;
}

.page-background .bg-layer.visible {
	opacity: 1;
}
</style>
